"use client";

import * as React from "react";
import { Check, Circle } from "lucide-react";
import { cn } from "../lib/cn";
import {
  passwordChecks,
  PASSWORD_REQUIREMENTS,
  type PasswordCheckResult,
  type PasswordRequirement,
} from "../lib/authValidation";

/**
 * PasswordRequirements - the live "rules met" checklist the reset-password
 * screen shows under its password field.
 *
 * Drives off the shared `passwordChecks` rule set (one source of truth with the
 * consuming screens, see `lib/authValidation`). Each row is sage-when-met:
 * green (`--success`) with a check once satisfied, muted with an open circle
 * until then. Pass `value` and it computes the checks for you, or pass
 * pre-computed `checks` + `requirements` for full control (e.g. a backend-driven
 * rule set).
 *
 * Accessibility: the list is a real `<ul>`; each row carries `data-met` so a
 * test (and assistive tech via the glyph's label) can read pass/fail, and the
 * glyph itself is `aria-hidden` with the rule text doing the announcing.
 *
 *   <PasswordRequirements value={pw} />
 *   <PasswordRequirements checks={checks} requirements={CUSTOM_RULES} />
 */
export interface PasswordRequirementsProps
  extends Omit<React.HTMLAttributes<HTMLUListElement>, "children"> {
  /** The current password. Used to compute `checks` when `checks` is not passed. */
  value?: string;
  /** Pre-computed per-rule booleans. Overrides `value` when provided. */
  checks?: PasswordCheckResult;
  /** The rules to render. Defaults to the shared `PASSWORD_REQUIREMENTS`. */
  requirements?: readonly PasswordRequirement[];
}

export const PasswordRequirements = React.forwardRef<HTMLUListElement, PasswordRequirementsProps>(
  ({ className, value = "", checks, requirements = PASSWORD_REQUIREMENTS, ...props }, ref) => {
    const resolved = checks ?? passwordChecks(value);

    return (
      <ul
        ref={ref}
        className={cn("m-0 grid list-none gap-[5px] p-0", className)}
        {...props}
      >
        {requirements.map((rule) => {
          const met = resolved[rule.key];
          return (
            <li
              key={rule.key}
              data-met={met || undefined}
              className={cn(
                "flex items-center gap-[7px] text-[11.5px]",
                met ? "text-success" : "text-muted-foreground",
              )}
            >
              <span aria-hidden className="flex w-3 justify-center">
                {met ? <Check className="h-3 w-3" /> : <Circle className="h-2.5 w-2.5" />}
              </span>
              {rule.label}
            </li>
          );
        })}
      </ul>
    );
  },
);
PasswordRequirements.displayName = "PasswordRequirements";
